import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Icon,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Text,
  Typography,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { farmersControllerList } from '../api/generated/farmers/farmers';
import type { FarmerDto } from '../api/generated/models';

const PAGE_SIZE = 20;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatGps(gps: FarmerDto['farmGps']): string | null {
  if (!gps) return null;
  return `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`;
}

/**
 * Cooperative farmer roster (coop_admin / admin). PILOT SCOPE: the
 * invite-by-phone and remove-member dialogs were cut — farmers are added via
 * the /farmers/new wizard only.
 */
export function FarmersScreen() {
  const { t } = useTranslation();

  const farmersQuery = useQuery({
    queryKey: ['farmers', { limit: PAGE_SIZE, offset: 0 }],
    queryFn: () => farmersControllerList({ limit: PAGE_SIZE, offset: 0 }),
  });

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Text variant="headline-medium" as="h2">
          {t('farmers.title')}
        </Text>
        <Link to="/farmers/new" style={{ textDecoration: 'none' }}>
          <Button variant="contained" startIcon={<Icon name="person_add" size={20} />}>
            {t('farmers.addFarmer')}
          </Button>
        </Link>
      </Box>

      {farmersQuery.isPending ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
            </Stack>
          </CardContent>
        </Card>
      ) : farmersQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => farmersQuery.refetch()}>
              {t('farmers.retry')}
            </Button>
          }
        >
          {t('farmers.loadError', { error: String(farmersQuery.error) })}
        </Alert>
      ) : farmersQuery.data.data.length === 0 ? (
        <Card>
          <CardHeader title={t('farmers.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('farmers.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <FarmersTable
          rows={farmersQuery.data.data}
          total={farmersQuery.data.pagination.total}
        />
      )}
    </Stack>
  );
}

interface FarmersTableProps {
  rows: FarmerDto[];
  total: number;
}

function FarmersTable({ rows, total }: FarmersTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Stack spacing={1.5}>
      <Text variant="body-medium" color="text.secondary">
        {t('farmers.count', { count: total })}
        {rows.length < total ? ' ' + t('farmers.showingFirst', { count: rows.length }) : ''}
      </Text>
      <TableContainer component={Card}>
        <Table size="small" aria-label={t('farmers.table.aria')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('farmers.table.name')}</TableCell>
              <TableCell>{t('farmers.table.phone')}</TableCell>
              <TableCell>{t('farmers.table.language')}</TableCell>
              <TableCell align="right">{t('farmers.table.trees')}</TableCell>
              <TableCell align="right">{t('farmers.table.capacity')}</TableCell>
              <TableCell>{t('farmers.table.gps')}</TableCell>
              <TableCell>{t('farmers.table.registered')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((farmer) => (
              <FarmerRow
                key={farmer.userId}
                farmer={farmer}
                onNavigate={() =>
                  navigate({ to: '/farmers/$userId', params: { userId: farmer.userId } })
                }
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

interface FarmerRowProps {
  farmer: FarmerDto;
  onNavigate: () => void;
}

function FarmerRow({ farmer, onNavigate }: FarmerRowProps) {
  const gps = formatGps(farmer.farmGps);

  return (
    <TableRow hover onClick={onNavigate} sx={{ cursor: 'pointer' }}>
      <TableCell>
        {/* Real anchor for keyboard / right-click "open in new tab";
            the row onClick handles the common click-anywhere case. */}
        <Link
          to="/farmers/$userId"
          params={{ userId: farmer.userId }}
          style={{ textDecoration: 'none', fontWeight: 600 }}
          onClick={(e) => e.stopPropagation()}
        >
          {farmer.displayName}
        </Link>
      </TableCell>
      <TableCell>{farmer.phone}</TableCell>
      <TableCell>
        <Chip kind="assist" color="neutral" label={farmer.preferredLang.toUpperCase()} />
      </TableCell>
      <TableCell align="right">{farmer.treeCount}</TableCell>
      <TableCell align="right">
        {farmer.estCapacityKg ?? (
          <Typography component="span" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {gps ? (
          <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
            {gps}
          </Typography>
        ) : (
          <Typography component="span" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>{dateFormatter.format(new Date(farmer.registeredAt))}</TableCell>
    </TableRow>
  );
}
